<template>
  <b-list-group class="container__responses">
    <b-list-group-item
      v-if="hasPreviousResponses && !isLoadingRepsonses"
      class="d-flex flex-row align-items-center text-primary rounded-0 border-left-0 border-right-0 py-3"
      :class="`${
        hidePreviousResponse ? 'btn__view-previous-response' : 'btn__hide-previous-response'
      }`"
      style="cursor: pointer"
      @click="onHideButtonClicked"
    >
      <font-awesome-icon icon="comments" class="mr-2 ml-2" />

      {{ `${hidePreviousResponse ? 'View' : 'Hide'} previous responses` }}
    </b-list-group-item>

    <b-list-group-item v-if="isLoadingRepsonses" class="text-center">
      <b-spinner small label="Loading responses"></b-spinner>
    </b-list-group-item>

    <template v-for="responses in allResponses">
      <b-list-group-item
        v-for="postResponse in responses"
        :key="`list__response-${postResponse.id}--hidden`"
        class="rounded-0 border-left-0 border-right-0"
      >
        <component
          :is="getResponseComponent(postResponse)"
          v-bind="getResponseComponentProps(postResponse)"
          :learning-outcome="learningOutcome"
          class="border-0 p-0"
        />
      </b-list-group-item>
    </template>
  </b-list-group>
</template>

<script>
import { map } from 'lodash';
import Post from '@/models/Post';
import PostSchedule from '@/models/PostSchedule';
import PostResponse from '@/models/PostResponse';
import ResponseSubmission from './ResponseSubmission';
import ResponseFeedback from './ResponseFeedback';
import { AssessmentStatus } from '@/models';
import { setTimeout } from 'timers';

export default {
  props: {
    post: {
      type: Post,
      required: true
    },

    postSchedule: {
      type: PostSchedule,
      required: true
    },

    assessmentStatus: {
      type: AssessmentStatus,
      default: null
    },

    learner: {
      type: Object,
      required: true
    },

    learningOutcome: {
      type: Object,
      default: () => {}
    },

    active: {
      type: Boolean,
      default: false
    }

    // postResponses: {
    //   type: Array,
    //   default: () => [],
    // },

    // alwaysDisplay: {
    //   type: Number,
    //   default: 1,
    // },
  },

  data() {
    return {
      isLoadingRepsonses: false,

      hidePreviousResponse: true,

      initialResponseIds: [],

      hiddenResponseIds: [],

      newResponseIds: []
    };
  },

  computed: {
    hasPreviousResponses() {
      return this.assessmentStatus
        ? this.assessmentStatus.count_change_to_review_pending > 0
        : false;
    },

    hiddenResponses() {
      return this.hidePreviousResponse ? [] : this.getResponseByIds(this.hiddenResponseIds);
    },

    initalResponses() {
      return this.getResponseByIds(this.initialResponseIds);
    },

    newResponses() {
      return this.getResponseByIds(this.newResponseIds);
    },

    alwayDisplayResponses() {
      return [...this.initalResponses, ...this.newResponses];
    },

    allResponses() {
      return [this.hiddenResponses, this.alwayDisplayResponses];
    }
  },

  watch: {
    active: {
      immediate: true,
      handler(v) {
        if (v) {
          this.fetchInitialResponses();
        } else {
          this.hidePreviousResponse = true;
          // this.clearData();
        }
      }
    }
  },

  methods: {
    async onHideButtonClicked() {
      await this.fetchHiddenResponses();

      this.hidePreviousResponse = !this.hidePreviousResponse;
    },

    async fetchHiddenResponses() {
      let promise;

      if (this.assessmentStatus && !this.hiddenResponseIds.length) {
        this.isLoadingRepsonses = true;

        try {
          promise = await PostResponse.api().fetchHiddenResponses(
            this.post.id,
            this.postSchedule.section_id,
            this.learner.id
          );
        } catch (error) {
          this.$toast.error('Cannot load previous responses, please try again');
        }

        setTimeout(() => {
          const { data = [] } = promise?.response;

          if (data.length) {
            this.hiddenResponseIds = map(data, 'id');
          }

          this.isLoadingRepsonses = false;
        }, 1000);
      }

      return promise;
    },

    async fetchInitialResponses() {
      let promise;

      if (this.assessmentStatus && !this.initialResponseIds.length) {
        this.isLoadingRepsonses = true;

        try {
          promise = await PostResponse.api().fetchInitialResponses(
            this.post.id,
            this.postSchedule.section_id,
            this.learner.id
          );
        } catch (error) {
          this.$toast.error('Cannot load responses, please try again');
        }

        setTimeout(() => {
          const { data = [] } = promise?.response;

          if (data.length) {
            this.initialResponseIds = map(data, 'id');
          }

          this.isLoadingRepsonses = false;
        }, 1000);
      }

      return promise;
    },

    getResponseByIds(responseIds) {
      return PostResponse.query()
        .with(['user', 'status', 'assessment_evaluations'])
        .whereIdIn(responseIds)
        .orderBy('created_at')
        .get();
    },

    addNewResponseId(postReponseId) {
      this.newResponseIds.push(postReponseId);
    },

    clearData() {
      this.hidePreviousResponse = true;
      this.initialResponseIds = [];
      this.hiddenResponseIds = [];
      this.newResponseIds = [];
    },

    refresh() {
      this.clearData();

      this.fetchInitialResponses();
    },

    getResponseComponent({ assessment_submission = null, assessment_evaluations = null } = {}) {
      if (assessment_submission) {
        return ResponseSubmission;
      } else if (assessment_evaluations) {
        return ResponseFeedback;
      }
    },

    getResponseComponentProps(postResponse) {
      let additionalProps = {};

      if (postResponse?.assessment_evaluations && postResponse.assessment_evaluations.length) {
        const {
          learning_outcome: learningOutcome,
          additional_criterion: additionalCriterions = []
        } = this.post;

        additionalProps = {
          ...additionalProps,
          learningOutcome,
          additionalCriterions
        };
      }

      return {
        postResponse,
        ...additionalProps
      };
    }
  }
};
</script>
